import { useFetch } from "@4uhub/lib4uhub";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ISyncPerson,
  ISyncPersonAddress,
  ISyncPersonDetails,
} from "../../../../models/sync-person";
import { getSyncPersonDetails } from "../../../../services/syncPersonDetails.service";

const translationPath = "page.4uBus.logs.syncPersons.";

// Códigos de ie_tipo_complemento (domínio 8 do Tasy). Não confundir com o
// IndividualAddressType da Library, que é outro domínio.
const KNOWN_ADDRESS_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30];

interface IProps {
  person: ISyncPerson;
  onClose: () => void;
}

const formatPhone = (
  ddi?: string | null,
  ddd?: string | null,
  number?: string | null,
) => [ddi && `+${ddi}`, ddd && `(${ddd})`, number].filter(Boolean).join(" ");

const SyncPersonDetailsModal: React.FC<IProps> = ({ person, onClose }) => {
  const { t, i18n } = useTranslation();
  const { sendRequest, loading } = useFetch(getSyncPersonDetails);

  const [details, setDetails] = useState<ISyncPersonDetails | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      setFailed(false);

      const { success, data } = await sendRequest({
        identifier: person.identifier,
      });

      if (success && data) {
        setDetails(data);
      } else {
        setFailed(true);
      }
    };

    fetchDetails();
  }, [sendRequest, person.identifier]);

  const formatDate = (value?: string | null, dateOnly = false) => {
    if (!value) return "-";

    return format(new Date(value), dateOnly ? "P" : "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? ptBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const formatAddressType = (code: number) =>
    KNOWN_ADDRESS_TYPES.includes(code)
      ? `${code} · ${t(translationPath + `address_types.${code}`)}`
      : t(translationPath + "address_type_unknown", { code });

  const renderValue = (label: string, value?: string | number | null) => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.4, wordBreak: "break-word" }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );

  const renderSection = (title: string, children: React.ReactNode) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  const grid = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
    },
    columnGap: 4,
    rowGap: 2,
  };

  const renderAddressRow = (address: ISyncPersonAddress) => (
    <TableRow key={address.identifier} hover>
      <TableCell>{formatAddressType(address.complementType)}</TableCell>
      <TableCell>{address.address || "-"}</TableCell>
      <TableCell>{address.number || "-"}</TableCell>
      <TableCell>{address.complement || "-"}</TableCell>
      <TableCell>{address.neighborhood || "-"}</TableCell>
      <TableCell>{address.city || "-"}</TableCell>
      <TableCell>
        {address.stateAbbreviation || address.state || "-"}
      </TableCell>
      <TableCell>{address.zipCode || "-"}</TableCell>
      <TableCell>{address.email || "-"}</TableCell>
      <TableCell>
        {formatPhone(address.phoneDdi, address.phoneDdd, address.phoneNumber) ||
          "-"}
      </TableCell>
      <TableCell>{address.system || "-"}</TableCell>
      <TableCell>{formatDate(address.lastModifiedNrec)}</TableCell>
      <TableCell>{formatDate(address.lastModified)}</TableCell>
    </TableRow>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      );
    }

    if (failed || !details) {
      return <Alert severity="error">{t(translationPath + "details_error")}</Alert>;
    }

    const { person: data, addresses } = details;

    return (
      <>
        {renderSection(
          t(translationPath + "section_identification"),
          <Box sx={grid}>
            {renderValue(t(translationPath + "name"), data.name)}
            {renderValue(t(translationPath + "cpf"), data.cpf)}
            {renderValue(
              t(translationPath + "birth_date"),
              formatDate(data.birthDate, true),
            )}
            {renderValue(
              t(translationPath + "tasy_identifier"),
              data.tasyIdentifier,
            )}
          </Box>,
        )}

        {renderSection(
          t(translationPath + "section_contact"),
          <Box sx={grid}>
            {renderValue(t(translationPath + "email"), data.email)}
            {renderValue(
              t(translationPath + "phone"),
              formatPhone(data.phoneDdi, data.phoneDdd, data.phoneNumber),
            )}
            {renderValue(
              t(translationPath + "cell_phone"),
              formatPhone(
                data.cellPhoneDdi,
                data.cellPhoneDdd,
                data.cellPhoneNumber,
              ),
            )}
          </Box>,
        )}

        {renderSection(
          t(translationPath + "section_origin"),
          <Box sx={grid}>
            {renderValue(t(translationPath + "system"), data.system)}
            {renderValue(
              t(translationPath + "system_nrec"),
              data.systemNrec,
            )}
            <Box />
            {renderValue(
              t(translationPath + "last_modified"),
              formatDate(data.lastModified),
            )}
            {renderValue(
              t(translationPath + "last_modified_nrec"),
              formatDate(data.lastModifiedNrec),
            )}
          </Box>,
        )}

        {renderSection(
          t(translationPath + "section_addresses"),
          addresses.length === 0 ? (
            <Typography color="text.secondary">
              {t(translationPath + "no_addresses")}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t(translationPath + "address_type")}</TableCell>
                    <TableCell>{t(translationPath + "address_street")}</TableCell>
                    <TableCell>{t(translationPath + "address_number")}</TableCell>
                    <TableCell>
                      {t(translationPath + "address_complement")}
                    </TableCell>
                    <TableCell>
                      {t(translationPath + "address_neighborhood")}
                    </TableCell>
                    <TableCell>{t(translationPath + "address_city")}</TableCell>
                    <TableCell>{t(translationPath + "address_state")}</TableCell>
                    <TableCell>{t(translationPath + "address_zip")}</TableCell>
                    <TableCell>{t(translationPath + "email")}</TableCell>
                    <TableCell>{t(translationPath + "phone")}</TableCell>
                    <TableCell>{t(translationPath + "system")}</TableCell>
                    <TableCell>{t(translationPath + "created")}</TableCell>
                    <TableCell>{t(translationPath + "last_modified")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{addresses.map(renderAddressRow)}</TableBody>
              </Table>
            </TableContainer>
          ),
        )}
      </>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogActions>
        <Button onClick={onClose}>X</Button>
      </DialogActions>
      <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
        {t(translationPath + "details_title")} - {person.name}
      </DialogTitle>
      <DialogContent
        sx={{ height: 560, width: "100%", marginTop: 0, overflow: "auto" }}
      >
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default SyncPersonDetailsModal;
